import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  onSnapshot, 
  query, 
  where, 
  orderBy,
  limit,
  getDocs
} from 'firebase/firestore';

class NetatmoFirestoreService {
  constructor() {
    this.listeners = new Map();
    this.cacheListeners = new Map();
  }

  /**
   * Écoute les comptes Netatmo en temps réel
   */
  subscribeToAccounts(callback) {
    const accountsRef = collection(db, 'netatmo_accounts');
    const q = query(
      accountsRef,
      where('isActive', '==', true),
      where('needsReauth', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const accounts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(accounts);
    }, (error) => {
      console.error('Erreur subscription comptes:', error);
      callback([]);
    });

    this.listeners.set('accounts', unsubscribe);
    return unsubscribe;
  }

  /**
   * Écoute les données de pluie en temps réel depuis le cache Firestore
   */
  subscribeToRainData(accountId, period, options = {}, callback) {
    const { startDate, endDate, selectedYear, selectedMonth } = options;
    
    // Générer la clé de cache
    const cacheKey = this.generateCacheKey(period, startDate, endDate, selectedYear, selectedMonth);
    const listenerKey = `rain_${accountId}_${cacheKey}`;
    
    // Si un listener existe déjà pour cette clé, le retourner directement
    if (this.cacheListeners.has(listenerKey)) {
      // Ne pas créer de nouveau listener, juste déclencher le callback avec les données actuelles
      const existingUnsubscribe = this.cacheListeners.get(listenerKey);
      
      // Créer un nouveau listener qui partage le même unsubscribe mais avec le nouveau callback
      const cacheRef = doc(db, 'netatmo_rain_cache', accountId, 'periods', cacheKey);
      const quickUnsubscribe = onSnapshot(cacheRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const now = Date.now();
          const lastApiUpdate = data.lastApiUpdate || data.createdAt;
          const createdAt = data.createdAt;
          let isStale = false;
          let apiAge = 0;
          let cacheAge = 0;
          
          if (lastApiUpdate) {
            apiAge = now - lastApiUpdate.toMillis();
            isStale = apiAge > 5 * 60 * 1000;
          } else {
            isStale = true;
          }
          
          if (createdAt) {
            cacheAge = now - createdAt.toMillis();
          }
          
          callback({
            success: true,
            data: data.data || { data: [], summary: {} },
            accountId,
            period,
            cached: true,
            stale: isStale,
            lastUpdate: createdAt ? createdAt.toDate().toISOString() : null,
            lastApiUpdate: lastApiUpdate ? lastApiUpdate.toDate().toISOString() : null,
            cacheAge: Math.floor(cacheAge / 1000),
            apiAge: Math.floor(apiAge / 1000)
          });
          
          // Unsubscribe immédiatement après le premier callback
          quickUnsubscribe();
        }
      });
      
      return existingUnsubscribe;
    }

    // Référence au document de cache
    const cacheRef = doc(db, 'netatmo_rain_cache', accountId, 'periods', cacheKey);
    
    const unsubscribe = onSnapshot(cacheRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        
        // Calculer si les données sont périmées basé sur lastApiUpdate
        const now = Date.now();
        let isStale = false;
        let cacheAge = 0;
        let apiAge = 0;
        
        // Utiliser lastApiUpdate si disponible, sinon fallback sur createdAt
        const lastApiUpdate = data.lastApiUpdate || data.createdAt;
        const createdAt = data.createdAt;
        
        if (lastApiUpdate) {
          apiAge = now - lastApiUpdate.toMillis();
          // Données périmées si pas de mise à jour API depuis plus de 5 minutes
          isStale = apiAge > 5 * 60 * 1000; // 5 minutes
        } else {
          isStale = true; // Pas de timestamp, considérer comme périmé
        }
        
        if (createdAt) {
          cacheAge = now - createdAt.toMillis();
        }
        
        callback({
          success: true,
          data: data.data || { data: [], summary: {} },
          accountId,
          period,
          cached: true,
          stale: isStale,
          lastUpdate: createdAt ? createdAt.toDate().toISOString() : null,
          lastApiUpdate: lastApiUpdate ? lastApiUpdate.toDate().toISOString() : null,
          cacheAge: Math.floor(cacheAge / 1000), // âge du cache en secondes
          apiAge: Math.floor(apiAge / 1000) // âge de la dernière MAJ API en secondes
        });
      } else {
        // Pas de données en cache
        callback({
          success: false,
          data: null,
          accountId,
          period,
          cached: false,
          stale: true,
          error: 'Aucune donnée en cache'
        });
      }
    }, (error) => {
      console.error('Erreur subscription données pluie:', error);
      callback({
        success: false,
        data: null,
        error: error.message
      });
    });

    this.cacheListeners.set(listenerKey, unsubscribe);
    return unsubscribe;
  }

  /**
   * Écoute les données agrégées de plusieurs comptes
   */
  subscribeToAggregatedData(accounts, period, options = {}, callback) {
    const { aggregationType = 'sum' } = options;
    const unsubscribes = [];
    const accountsData = new Map();
    
    // S'abonner à chaque compte
    accounts.forEach(account => {
      const unsubscribe = this.subscribeToRainData(
        account.id,
        period,
        options,
        (data) => {
          if (data.success && data.data) {
            accountsData.set(account.id, {
              accountId: account.id,
              stationName: account.stationName,
              location: account.metadata?.location,
              data: data.data,
              stale: data.stale,
              lastUpdate: data.lastUpdate
            });
          } else {
            accountsData.delete(account.id);
          }
          
          // Agréger et retourner les données
          const aggregated = this.aggregateData(
            Array.from(accountsData.values()),
            aggregationType
          );
          
          // Déterminer si au moins une donnée est périmée
          const anyStale = Array.from(accountsData.values()).some(d => d.stale);
          
          callback({
            success: true,
            data: aggregationType === 'separate' 
              ? Array.from(accountsData.values())
              : aggregated,
            period,
            aggregationType,
            accountsCount: accountsData.size,
            stale: anyStale,
            lastUpdate: new Date().toISOString()
          });
        }
      );
      unsubscribes.push(unsubscribe);
    });
    
    // Retourner une fonction pour désabonner tous les listeners
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }

  /**
   * Récupère les années disponibles depuis le cache
   */
  async getAvailableYears(accountId) {
    try {
      const periodsRef = collection(db, 'netatmo_rain_cache', accountId, 'periods');
      const snapshot = await getDocs(periodsRef);
      
      const years = new Set();
      const currentYear = new Date().getFullYear();
      
      snapshot.docs.forEach(doc => {
        const docId = doc.id;
        // Extraire l'année des clés de cache de type "year_2024"
        if (docId.startsWith('year_')) {
          const year = parseInt(docId.replace('year_', ''));
          if (!isNaN(year) && year >= currentYear - 10 && year <= currentYear) {
            years.add(year);
          }
        }
      });
      
      // Toujours inclure l'année courante
      years.add(currentYear);
      
      // Convertir en tableau trié (plus récent en premier)
      return Array.from(years).sort((a, b) => b - a);
    } catch (error) {
      console.error('Erreur getAvailableYears:', error);
      // Retourner les 2 dernières années par défaut
      const currentYear = new Date().getFullYear();
      return [currentYear, currentYear - 1];
    }
  }

  /**
   * Agrège les données de plusieurs comptes
   */
  aggregateData(accountsData, aggregationType) {
    if (!accountsData || accountsData.length === 0) {
      return { data: [], summary: {} };
    }
    
    // Créer une map des timestamps
    const timestampMap = new Map();
    
    accountsData.forEach(account => {
      if (account.data && account.data.data) {
        account.data.data.forEach(point => {
          if (!timestampMap.has(point.timestamp)) {
            timestampMap.set(point.timestamp, []);
          }
          timestampMap.get(point.timestamp).push(point.value || 0);
        });
      }
    });
    
    // Agréger selon le type
    const aggregatedData = [];
    let total = 0;
    let max = 0;
    
    Array.from(timestampMap.entries())
      .sort(([a], [b]) => a - b)
      .forEach(([timestamp, values]) => {
        let aggregatedValue;
        
        switch (aggregationType) {
          case 'sum':
            aggregatedValue = values.reduce((sum, val) => sum + val, 0);
            break;
          case 'average':
            aggregatedValue = values.reduce((sum, val) => sum + val, 0) / values.length;
            break;
          default:
            aggregatedValue = values.reduce((sum, val) => sum + val, 0);
        }
        
        aggregatedValue = Math.round(aggregatedValue * 10) / 10;
        
        aggregatedData.push({
          timestamp,
          value: aggregatedValue
        });
        
        total += aggregatedValue;
        max = Math.max(max, aggregatedValue);
      });
    
    return {
      data: aggregatedData,
      summary: {
        total: Math.round(total * 10) / 10,
        average: aggregatedData.length > 0 
          ? Math.round((total / aggregatedData.length) * 10) / 10 
          : 0,
        max: Math.round(max * 10) / 10,
        days: aggregatedData.filter(d => d.value > 0).length
      }
    };
  }

  /**
   * Génère une clé de cache unique
   */
  generateCacheKey(period, startDate, endDate, selectedYear, selectedMonth) {
    if (period === 'custom' && startDate && endDate) {
      return `${period}_${startDate}_${endDate}`;
    }
    if (period === 'year' && selectedYear) {
      if (selectedMonth) {
        return `year_${selectedYear}_month_${selectedMonth}`;
      }
      return `year_${selectedYear}`;
    }
    return period;
  }

  /**
   * Formate les données pour l'affichage selon la période
   */
  formatDataForPeriod(data, period) {
    if (!data || !data.data) {
      return { chartData: [], tableData: [], summary: data?.summary || {} };
    }
    
    const dataArray = data.data;
    let chartData = [];
    let tableData = [];
    
    switch (period) {
      case 'today':
      case '24h':
        // Données par heure
        chartData = dataArray.map(item => ({
          name: new Date(item.timestamp).toLocaleTimeString('fr-FR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          value: item.value || 0
        }));
        
        tableData = dataArray.map(item => ({
          période: new Date(item.timestamp).toLocaleTimeString('fr-FR'),
          fullDate: new Date(item.timestamp).toLocaleString('fr-FR'),
          total: item.value || 0
        }));
        break;
        
      case 'week':
        // Données par jour (jours avec pluie uniquement)
        const weeklyData = {};
        
        dataArray.forEach(item => {
          const date = new Date(item.timestamp).toLocaleDateString('fr-FR');
          if (!weeklyData[date]) {
            weeklyData[date] = 0;
          }
          weeklyData[date] += item.value || 0;
        });
        
        chartData = Object.entries(weeklyData)
          .filter(([_, value]) => value > 0)
          .map(([date, value]) => ({
            name: date,
            value: Math.round(value * 1000) / 1000
          }));
        
        tableData = chartData.map(item => ({
          période: item.name,
          fullDate: item.name,
          total: item.value
        }));
        break;
        
      case 'month':
        // Données par jour sur 30 jours
        const monthlyData = {};
        dataArray.forEach(item => {
          const date = new Date(item.timestamp).toLocaleDateString('fr-FR');
          if (!monthlyData[date]) {
            monthlyData[date] = 0;
          }
          monthlyData[date] += item.value || 0;
        });
        
        chartData = Object.entries(monthlyData).map(([date, value]) => ({
          name: date,
          value: Math.round(value * 10) / 10
        }));
        
        tableData = chartData.map(item => ({
          période: item.name,
          fullDate: item.name,
          total: item.value
        }));
        break;
        
      case 'year':
        // Les données sont déjà agrégées par mois côté backend
        chartData = dataArray.map(item => ({
          name: item.monthName || new Date(item.timestamp).toLocaleDateString('fr-FR', { 
            month: 'long', 
            year: 'numeric' 
          }),
          value: Math.round((item.value || 0) * 10) / 10,
          month: item.month,
          year: item.year
        }));
        
        tableData = chartData.map(item => ({
          période: item.name,
          fullDate: item.name,
          total: item.value
        }));
        break;
        
      default:
        chartData = dataArray.map(item => ({
          name: new Date(item.timestamp).toLocaleDateString('fr-FR'),
          value: item.value || 0
        }));
        
        tableData = chartData.map(item => ({
          période: item.name,
          fullDate: item.name,
          total: item.value
        }));
    }
    
    return {
      chartData,
      tableData,
      summary: data.summary || {}
    };
  }

  /**
   * Nettoie tous les listeners
   */
  cleanup() {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners.clear();
    
    this.cacheListeners.forEach(unsubscribe => unsubscribe());
    this.cacheListeners.clear();
  }
}

export default new NetatmoFirestoreService();